import {
  ArrowUpRight,
  Calendar,
  CheckCircle2,
  ExternalLink,
  Loader2,
  MessageSquare,
  RefreshCw,
} from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { enhancedApi as externalTaskApi } from '@/services/api/generated/externalTaskService';

export type PushChangeType = 'status' | 'due_date' | 'comment';

export interface PushChangeData {
  status?: string; // Canonical status name for status changes
  due_date?: string; // ISO string for due date changes
  comment?: string; // Comment text for comment changes
  source_task_update_id?: string; // TaskUpdate ID for tracking comment origin
}

/** A single change item with its type, data, and eligible external tasks */
export interface PushChangeItem {
  changeType: PushChangeType;
  changeData: PushChangeData;
  /** External tasks that are eligible for this specific change */
  eligibleTasks: ExternalTaskForPush[];
  /** Whether to group tasks by their source internal task (for multi-task mode) */
  groupByInternalTask?: boolean;
}

// Minimal external task type for push changes - only the fields we need
export interface ExternalTaskForPush {
  id: string;
  name: string;
  status: string;
  source: string;
  /** Source internal task ID (for multi-task mode) */
  sourceInternalTaskId?: string;
  /** Source internal task name (for multi-task mode) */
  sourceInternalTaskName?: string;
}

interface PushChangesDialogProps {
  open: boolean;
  onClose: () => void;
  onPushComplete?: () => void;
  /** Multiple changes to push - each with its own eligible tasks */
  changes: PushChangeItem[];
}

/**
 * Maps internal task canonical status to external task status.
 */
function mapInternalToExternalStatus(
  canonicalStatus: string,
): 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' {
  const statusMap: Record<string, 'PENDING' | 'IN_PROGRESS' | 'COMPLETED'> = {
    Pending: 'PENDING',
    Blocked: 'PENDING',
    'In Progress': 'IN_PROGRESS',
    Completed: 'COMPLETED',
    'Not Applicable': 'COMPLETED',
    Closed: 'COMPLETED',
  };
  return statusMap[canonicalStatus] ?? 'PENDING';
}

/**
 * Get change type icon
 */
function getChangeIcon(changeType: PushChangeType) {
  switch (changeType) {
    case 'status':
      return RefreshCw;
    case 'due_date':
      return Calendar;
    case 'comment':
      return MessageSquare;
    default:
      return ArrowUpRight;
  }
}

/**
 * Get formatted change value for display
 */
function getChangeValue(
  changeType: PushChangeType,
  changeData: PushChangeData,
): string {
  switch (changeType) {
    case 'status':
      return changeData.status || 'Unknown';
    case 'due_date':
      if (!changeData.due_date) return 'Not set';
      return new Date(
        `${changeData.due_date.split('T')[0]}T00:00:00`,
      ).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    case 'comment':
      if (!changeData.comment) return '';
      return changeData.comment.length > 60
        ? `${changeData.comment.slice(0, 60)}...`
        : changeData.comment;
    default:
      return '';
  }
}

/**
 * Get change type label
 */
function getChangeLabel(changeType: PushChangeType): string {
  switch (changeType) {
    case 'status':
      return 'Status Update';
    case 'due_date':
      return 'Due Date';
    case 'comment':
      return 'Comment';
    default:
      return 'Change';
  }
}

/** Groups tasks by their source internal task */
interface TaskGroup {
  internalTaskId: string;
  internalTaskName: string;
  tasks: ExternalTaskForPush[];
}

function groupTasksByInternalTask(
  tasks: ExternalTaskForPush[],
): TaskGroup[] | null {
  // Check if any tasks have source info
  const hasSourceInfo = tasks.some((t) => t.sourceInternalTaskId);
  if (!hasSourceInfo) return null;

  const groups = new Map<string, TaskGroup>();

  tasks.forEach((task) => {
    const key = task.sourceInternalTaskId || 'unknown';
    if (!groups.has(key)) {
      groups.set(key, {
        internalTaskId: task.sourceInternalTaskId || '',
        internalTaskName: task.sourceInternalTaskName || 'Unknown Task',
        tasks: [],
      });
    }
    const group = groups.get(key);
    if (group) {
      group.tasks.push(task);
    }
  });

  return Array.from(groups.values());
}

/** Single task item button */
const TaskItemButton: React.FC<{
  task: ExternalTaskForPush;
  isSelected: boolean;
  isCompleted: boolean;
  isPushing: boolean;
  onToggle: () => void;
}> = ({ task, isSelected, isCompleted, isPushing, onToggle }) => {
  return (
    <button
      type="button"
      onClick={() => !isPushing && onToggle()}
      disabled={isPushing}
      className={`w-full text-left rounded-lg p-2.5 transition-all duration-150 ${
        isSelected
          ? 'bg-blue-50 ring-2 ring-blue-500 ring-inset'
          : 'bg-white border border-slate-200 hover:border-slate-300'
      } ${isPushing ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <div className="flex items-center gap-2.5">
        {/* Custom Checkbox */}
        <div
          className={`flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-all duration-150 ${
            isSelected
              ? 'bg-blue-500 border-blue-500'
              : 'border-slate-300 bg-white'
          }`}
        >
          {isSelected && (
            <svg
              className="w-2.5 h-2.5 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={3}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          )}
        </div>

        {/* Task Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Link
              to={`/external-tasks/${task.id}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-sm font-medium text-slate-900 truncate hover:text-blue-600 hover:underline transition-colors"
            >
              {task.name}
            </Link>
            {isCompleted && (
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
              {task.status}
            </span>
            <span className="inline-flex items-center gap-1 text-[10px] text-slate-500">
              <ExternalLink className="w-2.5 h-2.5" />
              {task.source}
            </span>
          </div>
        </div>
      </div>
    </button>
  );
};

/** Renders task list for a change, optionally grouped by internal task */
const TaskListForChange: React.FC<{
  change: PushChangeItem;
  changeIndex: number;
  selectedItems: Set<string>;
  completedItems: Set<string>;
  isPushing: boolean;
  onToggleItem: (changeIndex: number, taskId: string) => void;
}> = ({
  change,
  changeIndex,
  selectedItems,
  completedItems,
  isPushing,
  onToggleItem,
}) => {
  const groupedTasks = React.useMemo(() => {
    if (!change.groupByInternalTask) return null;
    return groupTasksByInternalTask(change.eligibleTasks);
  }, [change.eligibleTasks, change.groupByInternalTask]);

  // Grouped rendering
  if (groupedTasks) {
    return (
      <>
        {groupedTasks.map((group) => (
          <div key={group.internalTaskId} className="mb-2 last:mb-0">
            <div className="text-[10px] font-medium text-slate-500 px-2 py-1.5 bg-slate-50 rounded-t border-b border-slate-200/60">
              {group.internalTaskName}
            </div>
            <div className="space-y-1.5 border-l-2 border-slate-200 ml-2 pl-2 pt-1.5">
              {group.tasks.map((task) => {
                const itemKey = `${changeIndex}-${task.id}`;
                return (
                  <TaskItemButton
                    key={task.id}
                    task={task}
                    isSelected={selectedItems.has(itemKey)}
                    isCompleted={completedItems.has(itemKey)}
                    isPushing={isPushing}
                    onToggle={() => onToggleItem(changeIndex, task.id)}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </>
    );
  }

  // Flat rendering (existing behavior)
  return (
    <>
      {change.eligibleTasks.map((task) => {
        const itemKey = `${changeIndex}-${task.id}`;
        return (
          <TaskItemButton
            key={task.id}
            task={task}
            isSelected={selectedItems.has(itemKey)}
            isCompleted={completedItems.has(itemKey)}
            isPushing={isPushing}
            onToggle={() => onToggleItem(changeIndex, task.id)}
          />
        );
      })}
    </>
  );
};

export const PushChangesDialog: React.FC<PushChangesDialogProps> = ({
  open,
  onClose,
  onPushComplete,
  changes,
}) => {
  // Track selected task IDs per change index
  // Key: `${changeIndex}-${taskId}`, Value: boolean (selected)
  const [selectedItems, setSelectedItems] = React.useState<Set<string>>(
    new Set(),
  );
  const [isPushing, setIsPushing] = React.useState(false);
  const [completedItems, setCompletedItems] = React.useState<Set<string>>(
    new Set(),
  );

  // Reset state when dialog opens with new changes
  React.useEffect(() => {
    if (open) {
      // Select all items by default
      const allItems = new Set<string>();
      changes.forEach((change, changeIndex) => {
        change.eligibleTasks.forEach((task) => {
          allItems.add(`${changeIndex}-${task.id}`);
        });
      });
      setSelectedItems(allItems);
      setCompletedItems(new Set());
    }
  }, [open, changes]);

  // RTK Query mutations
  const [addComment] = externalTaskApi.useExternalTaskAddCommentMutation();
  const [changeStatus] = externalTaskApi.useExternalTaskChangeStatusMutation();
  const [changeDueDate] =
    externalTaskApi.useExternalTaskChangeDueDateMutation();

  const handleToggleItem = (changeIndex: number, taskId: string) => {
    const key = `${changeIndex}-${taskId}`;
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const handlePush = async () => {
    if (selectedItems.size === 0) return;

    setIsPushing(true);

    // Helper to create the appropriate mutation call for a specific change
    const createMutationPromise = (
      changeType: PushChangeType,
      changeData: PushChangeData,
      externalTaskId: string,
    ) => {
      switch (changeType) {
        case 'status':
          if (changeData.status) {
            const mappedStatus = mapInternalToExternalStatus(changeData.status);
            return changeStatus({
              id: externalTaskId,
              changeStatusPayload: { status: mappedStatus },
            }).unwrap();
          }
          return Promise.resolve();
        case 'due_date':
          if (changeData.due_date) {
            // Extract just the date part (YYYY-MM-DD) to match ExternalTaskDetail format
            const dateOnly = changeData.due_date.split('T')[0];
            return changeDueDate({
              id: externalTaskId,
              changeDueDatePayload: { due_date: dateOnly },
            }).unwrap();
          }
          return Promise.resolve();
        case 'comment':
          if (changeData.comment) {
            return addComment({
              id: externalTaskId,
              addCommentPayload: {
                comment: changeData.comment,
                source_task_update_id: changeData.source_task_update_id,
              },
            }).unwrap();
          }
          return Promise.resolve();
        default:
          return Promise.resolve();
      }
    };

    // Build list of all mutations to execute
    const mutations: {
      key: string;
      changeIndex: number;
      taskId: string;
      changeType: PushChangeType;
      changeData: PushChangeData;
    }[] = [];

    changes.forEach((change, changeIndex) => {
      change.eligibleTasks.forEach((task) => {
        const key = `${changeIndex}-${task.id}`;
        if (selectedItems.has(key)) {
          mutations.push({
            key,
            changeIndex,
            taskId: task.id,
            changeType: change.changeType,
            changeData: change.changeData,
          });
        }
      });
    });

    // Execute all mutations in parallel
    const results = await Promise.allSettled(
      mutations.map((mutation) =>
        createMutationPromise(
          mutation.changeType,
          mutation.changeData,
          mutation.taskId,
        ).then(() => mutation.key),
      ),
    );

    // Process results
    const successfulKeys: string[] = [];
    let failCount = 0;

    results.forEach((result) => {
      if (result.status === 'fulfilled') {
        successfulKeys.push(result.value);
      } else {
        failCount += 1;
      }
    });

    setCompletedItems((prev) => new Set([...prev, ...successfulKeys]));
    setIsPushing(false);

    const successCount = successfulKeys.length;

    // Show toast based on results
    if (failCount === 0) {
      toast.success(
        `Syncing ${successCount} change${successCount !== 1 ? 's' : ''} to external tasks`,
      );
    } else if (successCount === 0) {
      toast.error('Failed to push changes to external tasks');
    } else {
      toast.warning(
        `Pushed ${successCount} change${successCount !== 1 ? 's' : ''}, ${failCount} failed`,
      );
    }

    // Notify parent that push is complete so it can trigger refetch
    onPushComplete?.();
    onClose();
  };

  const noneSelected = selectedItems.size === 0;

  // Calculate total counts
  const totalItems = changes.reduce(
    (acc, change) => acc + change.eligibleTasks.length,
    0,
  );

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[500px] p-0 gap-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="text-lg font-semibold text-slate-900">
            Sync to External Tasks
          </DialogTitle>
        </DialogHeader>

        {/* Changes List */}
        <div className="px-6 pb-2 max-h-[400px] overflow-y-auto">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-slate-700">
              {changes.length === 1 ? 'Change to sync' : 'Changes to sync'}
            </span>
            <span className="text-xs text-slate-500">
              {selectedItems.size} of {totalItems} selected
            </span>
          </div>

          <div className="space-y-4">
            {changes.map((change, changeIndex) => {
              const ChangeIcon = getChangeIcon(change.changeType);

              return (
                <div
                  key={changeIndex}
                  className="bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-xl border border-slate-200/60 overflow-hidden"
                >
                  {/* Change Header */}
                  <div className="p-3 border-b border-slate-200/60">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white shadow-sm border border-slate-200/60 flex items-center justify-center">
                        <ChangeIcon className="w-4 h-4 text-slate-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                          {getChangeLabel(change.changeType)}
                        </div>
                        <div className="text-sm font-semibold text-slate-900 break-words">
                          {getChangeValue(change.changeType, change.changeData)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tasks for this change */}
                  <div className="p-2 space-y-1.5">
                    <TaskListForChange
                      change={change}
                      changeIndex={changeIndex}
                      selectedItems={selectedItems}
                      completedItems={completedItems}
                      isPushing={isPushing}
                      onToggleItem={handleToggleItem}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="px-6 py-4 bg-slate-50/50 border-t border-slate-100 mt-4">
          <div className="flex items-center gap-3 w-full">
            <Button
              variant="ghost"
              onClick={onClose}
              disabled={isPushing}
              className="flex-1 h-11 text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            >
              Skip
            </Button>
            <Button
              onClick={handlePush}
              disabled={noneSelected || isPushing}
              className="flex-1 h-11 bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
            >
              {isPushing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <ArrowUpRight className="h-4 w-4 mr-2" />
                  Sync {selectedItems.size} Change
                  {selectedItems.size !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
